import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    CropOriginal,
    ArrowForward,
    ExpandMore,
    Terrain,
    Route as RouteIcon,
    Map as MapIcon
} from '@mui/icons-material';
import TopNav from './TopNav.jsx';
import Footer from './Footer.jsx';
import useDocumentTitle from '../utils/useDocumentTitle';
import {
    PALETTE,
    GRADIENTS,
    BORDERS,
    BORDER_COLORS,
    SHADOWS,
    TINTS,
    BG_PATTERN,
    alpha
} from '../theme';

// The four offerings. Each tile pairs a REAL example (made by the app's own
// rendering pipeline — see public/brand/examples) with the words that explain
// it: the picture and the copy live together in one card.
const OFFERINGS = [
    {
        key: 'travel',
        to: '/travel-maps',
        icon: <MapIcon sx={{ fontSize: 24 }} />,
        title: 'Travel Maps',
        img: '/brand/examples/travel-usa.png',
        accent: PALETTE.primary,
        desc: "Mark every country, US state, national park and county you've visited.",
        bullets: ['24+ maps with a live visited count', 'Pick your color, see your progress']
    },
    {
        key: 'trail',
        to: '/trail-maps',
        icon: <Terrain sx={{ fontSize: 24 }} />,
        title: 'Trail Maps',
        img: '/brand/examples/trail-tmb.jpg',
        accent: PALETTE.accent,
        desc: 'Turn a GPS track — one file, or several for a multi-day hike — into a map.',
        bullets: ['Drawn on a topographic basemap', 'Distance, elevation gain & profile']
    },
    {
        key: 'road',
        to: '/road-trips',
        icon: <RouteIcon sx={{ fontSize: 24 }} />,
        title: 'Road Trips',
        img: '/brand/examples/roadtrip-sw.jpg',
        accent: PALETTE.primary,
        desc: 'Search your stops, drop them in order, and we draw the drive between them.',
        bullets: ['The route follows real roads', 'Search any city, landmark or address']
    },
    {
        key: 'photo',
        to: '/photo-shaper',
        icon: <CropOriginal sx={{ fontSize: 24 }} />,
        title: 'Photo Shaper',
        img: '/brand/examples/photo-australia.png',
        accent: PALETTE.text,
        desc: "Crop any photo into the silhouette of a place that matters to you.",
        bullets: ['50 states · 100+ countries, parks, islands', 'Pan, zoom & frame inside the shape']
    }
];

const POPULAR_MAPS = [
    { map: 'usa',         label: '🇺🇸 USA States' },
    { map: 'world',       label: '🗺️ World' },
    { map: 'europe',      label: '🏰 Europe' },
    { map: 'parks',       label: '🦬 US National Parks' },
    { map: 'canada',      label: '🇨🇦 Canada' },
    { map: 'uk-counties', label: '🇬🇧 UK Counties' },
    { map: 'asia',        label: '🐉 Asia' }
];

const FAQS = [
    {
        q: 'Do I need to create an account?',
        a: 'No — building maps, shaping photos, and downloading or ordering the result never needs an account. Signing up is optional: it lets you save your maps to "My Maps" and reopen them later, on any device.'
    },
    {
        q: 'Where is my map saved? Can I come back to it later?',
        a: 'Create an account and click Save — your map goes to "My Maps", where you can reopen it with all its settings to download or order again, from any device. Without an account, selections live only in the current browser tab, so download the image if you want to keep it.'
    },
    {
        q: 'How does ordering products work?',
        a: 'When you click Order on any product card, we upload your map to our printing partner (Zazzle) and open their checkout in a new tab. Payment, shipping, returns, and customer service are handled directly by them.'
    },
    {
        q: 'How long does shipping take?',
        a: 'Orders are printed and shipped by Zazzle. Standard production is usually 1–3 business days; delivery depends on your address and the shipping option you choose at checkout.'
    },
    {
        q: 'My country, state, or region is not on the map. Can you add it?',
        a: 'Probably yes — we add new maps regularly. Email mapshaped@gmail.com with the region you would like to see and we will look at adding it.'
    },
    {
        q: 'How is my photo handled when I use Photo Shaper?',
        a: 'Your photo is processed in your browser. It is only uploaded to our storage if you save the map to your account (so it can be reopened) or order a product (so it can be printed). We do not use uploaded photos for anything else.'
    }
];

const SectionHeading = ({ eyebrow, title, subtitle, align = 'center' }) => (
    <Box sx={{ textAlign: align, mb: { xs: 3, sm: 4 } }}>
        {eyebrow && (
            <Typography
                sx={{
                    color: PALETTE.primary,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    mb: 1
                }}
            >
                {eyebrow}
            </Typography>
        )}
        <Typography
            component="h2"
            sx={{
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                fontWeight: 800,
                color: PALETTE.text,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                mb: subtitle ? 1 : 0
            }}
        >
            {title}
        </Typography>
        {subtitle && (
            <Typography
                sx={{
                    color: PALETTE.textBody,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    maxWidth: 560,
                    mx: align === 'center' ? 'auto' : 0,
                    lineHeight: 1.5
                }}
            >
                {subtitle}
            </Typography>
        )}
    </Box>
);

// One offering = the real example AND its explanation, together in a single
// card: a preview at the top, then icon + title + a line + two points + a link.
const OfferingTile = ({ offering }) => (
    <Box
        component={RouterLink}
        to={offering.to}
        sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            textDecoration: 'none',
            backgroundColor: PALETTE.surface,
            border: BORDERS.soft,
            boxShadow: SHADOWS.card,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 14px 30px rgba(26, 20, 16, 0.12)'
            },
            '&:hover .tile-cta': { gap: 1 }
        }}
    >
        {/* preview */}
        <Box
            sx={{
                height: { xs: 180, sm: 210 },
                backgroundColor: PALETTE.surfaceSubtle,
                borderBottom: BORDERS.faint,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box
                component="img"
                src={offering.img}
                alt={`${offering.title} example`}
                loading="lazy"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
            />
        </Box>

        {/* explanation */}
        <Box sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box
                    sx={{
                        width: 46,
                        height: 46,
                        flexShrink: 0,
                        borderRadius: 2.5,
                        background: `linear-gradient(135deg, ${offering.accent}, ${offering.accent}dd)`,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 8px 18px ${offering.accent}44`
                    }}
                >
                    {offering.icon}
                </Box>
                <Typography
                    component="h3"
                    sx={{ fontWeight: 800, fontSize: '1.25rem', color: PALETTE.text, letterSpacing: '-0.01em' }}
                >
                    {offering.title}
                </Typography>
            </Box>

            <Typography sx={{ color: PALETTE.textBody, fontSize: '0.95rem', lineHeight: 1.5, mb: 1.5 }}>
                {offering.desc}
            </Typography>

            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
                {offering.bullets.map((b) => (
                    <Box
                        key={b}
                        component="li"
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            color: PALETTE.textMuted,
                            fontSize: '0.88rem',
                            py: 0.3
                        }}
                    >
                        <Box sx={{ mt: '7px', width: 5, height: 5, borderRadius: '50%', backgroundColor: offering.accent, flexShrink: 0 }} />
                        {b}
                    </Box>
                ))}
            </Box>

            <Box
                className="tile-cta"
                sx={{
                    mt: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: PALETTE.primaryDark,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    transition: 'gap 0.15s ease'
                }}
            >
                Open {offering.title} <ArrowForward sx={{ fontSize: 16 }} />
            </Box>
        </Box>
    </Box>
);

const HomePage = () => {
    useDocumentTitle('Map Shaper — Travel Maps, Trail Maps, Road Trips & Photo Silhouettes');
    return (
    <Box
        sx={{
            minHeight: '100vh',
            '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
            backgroundColor: PALETTE.page,
            display: 'flex',
            flexDirection: 'column'
        }}
    >
        <TopNav />

        <Box component="main" sx={{ flex: 1 }}>
            {/* ─── HERO ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: PALETTE.surface,
                    borderBottom: BORDERS.faint,
                    backgroundImage: `radial-gradient(${alpha('primary', 0.18)} 1px, transparent 1px)`,
                    backgroundSize: BG_PATTERN.backgroundSize,
                    backgroundPosition: BG_PATTERN.backgroundPosition
                }}
            >
                <Box
                    sx={{
                        maxWidth: 760,
                        mx: 'auto',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 6, sm: 8, md: 9 },
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{
                            fontSize: { xs: '2.25rem', sm: '3rem', md: '3.4rem' },
                            fontWeight: 800,
                            color: PALETTE.text,
                            letterSpacing: '-0.03em',
                            lineHeight: 1.05,
                            mb: 2
                        }}
                    >
                        Map everywhere{' '}
                        <Box
                            component="span"
                            sx={{
                                background: GRADIENTS.headline,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text'
                            }}
                        >
                            you've been.
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            color: PALETTE.textBody,
                            fontSize: { xs: '1.05rem', sm: '1.2rem' },
                            lineHeight: 1.5,
                            maxWidth: 600,
                            mx: 'auto',
                            mb: 3.5
                        }}
                    >
                        Travel maps, trail maps, road trips and photo silhouettes — made right
                        in your browser. Download the image, or order it on a cup, t-shirt and more.
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1.5,
                            justifyContent: 'center'
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/travel-maps"
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForward />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                background: GRADIENTS.primaryDeep,
                                boxShadow: SHADOWS.cta,
                                '&:hover': {
                                    background: GRADIENTS.primaryDeep,
                                    filter: 'brightness(1.05)',
                                    boxShadow: SHADOWS.ctaHover,
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Open Travel Maps
                        </Button>
                        <Button
                            component={RouterLink}
                            to="/photo-shaper"
                            variant="outlined"
                            size="large"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                borderColor: alpha('text', 0.15),
                                color: PALETTE.text,
                                backgroundColor: PALETTE.surface,
                                '&:hover': {
                                    borderColor: PALETTE.primary,
                                    color: PALETTE.primaryDark,
                                    backgroundColor: PALETTE.surface
                                }
                            }}
                        >
                            Try Photo Shaper
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ─── THE FOUR THINGS YOU CAN MAKE (image + words together) ── */}
            <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 6, sm: 8 } }}>
                <SectionHeading
                    eyebrow="What you can do"
                    title="Four ways to map what matters"
                    subtitle="Map where you've been, draw a hike or a road trip, or shape a photo — every example below is the real thing you'd make."
                />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: { xs: 2.5, sm: 3 }
                    }}
                >
                    {OFFERINGS.map((o) => (
                        <OfferingTile key={o.key} offering={o} />
                    ))}
                </Box>
            </Box>

            {/* ─── POPULAR MAPS ─────────────────────────────────────── */}
            <Box
                sx={{
                    backgroundColor: PALETTE.surface,
                    borderTop:    BORDERS.faint,
                    borderBottom: BORDERS.faint
                }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 5, sm: 6 } }}>
                    <SectionHeading
                        eyebrow="Jump right in"
                        title="Popular travel maps"
                        subtitle="The maps people open most. Pick one and start clicking — your progress saves while you're on the page."
                    />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.25 }}>
                        {POPULAR_MAPS.map(({ map, label }) => (
                            <Chip
                                key={map}
                                component={RouterLink}
                                to={`/travel-maps?map=${map}`}
                                clickable
                                label={label}
                                sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    height: 40,
                                    px: 1,
                                    backgroundColor: PALETTE.page,
                                    color: PALETTE.text,
                                    border: BORDERS.soft,
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        backgroundColor: TINTS.primary,
                                        borderColor: BORDER_COLORS.primary,
                                        color: PALETTE.primaryDark
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ─── FAQ ──────────────────────────────────────────────── */}
            <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 6, sm: 8 } }}>
                <SectionHeading eyebrow="Good to know" title="Common questions" />

                <Box>
                    {FAQS.map((faq, i) => (
                        <Accordion
                            key={i}
                            disableGutters
                            elevation={0}
                            sx={{
                                backgroundColor: PALETTE.surface,
                                border: BORDERS.soft,
                                borderRadius: 2,
                                mb: 1.25,
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { borderColor: BORDER_COLORS.primary }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                sx={{
                                    px: { xs: 2, sm: 3 },
                                    py: 0.5,
                                    '& .MuiAccordionSummary-content': { my: 1.5 }
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, color: PALETTE.text }}>
                                    {faq.q}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: 0 }}>
                                <Typography sx={{ color: PALETTE.textBody, lineHeight: 1.6 }}>
                                    {faq.a}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Box>
        </Box>

        <Footer />
    </Box>
    );
};

export default HomePage;
